import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import type {
  SolarShareStateDto,
  SolarShareViewsDto,
} from "../dto/solar-presentation.dto"
import { solarPresentationService } from "../services/solar-presentation.service"

export function useSolarShareState(
  proposalId: string | undefined,
): UseQueryResult<SolarShareStateDto> {
  return useQuery({
    queryKey: queryKeys.solarProposal.share(proposalId ?? ``),
    queryFn: () => solarPresentationService.getShareState(proposalId ?? ``),
    enabled: Boolean(proposalId),
    retry: 1,
    // Another session may have unpublished the link, or pricing moved, since
    // this cache was written: every open asks the server.
    staleTime: 0,
  })
}

export function useSolarShareViews(
  proposalId: string | undefined,
): UseQueryResult<SolarShareViewsDto> {
  return useQuery({
    queryKey: queryKeys.solarProposal.shareViews(proposalId ?? ``),
    queryFn: () => solarPresentationService.getViews(proposalId ?? ``),
    enabled: Boolean(proposalId),
    retry: 1,
    // Views arrive while the drawer is closed; a reopen must show them.
    staleTime: 0,
  })
}

/**
 * Publish and Unpublish answer with the whole ShareState, which replaces the
 * cached one. A refusal means the client's picture is behind the server's,
 * so it is read again.
 */
const useShareCommand = (
  run: (proposalId: string) => Promise<SolarShareStateDto>,
): UseMutationResult<SolarShareStateDto, unknown, string> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: run,
    onSuccess: (state, proposalId) => {
      queryClient.setQueryData(queryKeys.solarProposal.share(proposalId), state)
      void queryClient.invalidateQueries({
        queryKey: queryKeys.solarProposal.shareViews(proposalId),
      })
    },
    onError: (_error, proposalId) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.solarProposal.share(proposalId),
        exact: true,
      })
    },
  })
}

export function usePublishSolarShare(): UseMutationResult<
  SolarShareStateDto,
  unknown,
  string
> {
  return useShareCommand((proposalId) =>
    solarPresentationService.publish(proposalId),
  )
}

export function useUnpublishSolarShare(): UseMutationResult<
  SolarShareStateDto,
  unknown,
  string
> {
  return useShareCommand((proposalId) =>
    solarPresentationService.unpublish(proposalId),
  )
}

/** Without `years`, the PDF's dial opens on the seller page's own default. */
export type ExportSolarPdfVariables = { proposalId: string; years?: number }

/** The PDF bytes are the answer: nothing is cached. A refusal re-reads readiness. */
export function useExportSolarPdf(): UseMutationResult<
  Blob,
  unknown,
  ExportSolarPdfVariables
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ proposalId, years }: ExportSolarPdfVariables) =>
      solarPresentationService.exportPdf(proposalId, years),
    onError: (_error, { proposalId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.solarProposal.share(proposalId),
        exact: true,
      })
    },
  })
}
